import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  QuoteFormSettings,
  QuoteFormSettingsCreateDto,
  QuoteFormSettingsResponse,
  QuoteFormSettingsFilters,
  QuoteFormSettingsSort,
  QuoteFormSettingsState
} from "../../modules/shared/model/quoteFormSettings.interfaces";
import {
  createQuoteFormSettings as createQuoteFormSettingsAction,
  updateQuoteFormSettings as updateQuoteFormSettingsAction,
  getQuoteFormSettingsById as getQuoteFormSettingsByIdAction,
  getQuoteFormSettings as getQuoteFormSettingsAction,
  getQuoteFormSettingsWithFilters as getQuoteFormSettingsWithFiltersAction,
  deleteQuoteFormSettings as deleteQuoteFormSettingsAction
} from "../Actions/QuoteFormSettingsActions";

// Quote Form Settings Async Thunks
export const createQuoteFormSettings = createAsyncThunk(
  "quoteFormSettings/create",
  async (data: QuoteFormSettingsCreateDto, { rejectWithValue }) => {
    try {
      return await createQuoteFormSettingsAction(data);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to create quote form settings");
    }
  }
);

export const updateQuoteFormSettings = createAsyncThunk(
  "quoteFormSettings/update",
  async ({ id, data }: { id: string; data: Partial<QuoteFormSettingsCreateDto> }, { rejectWithValue }) => {
    try {
      return await updateQuoteFormSettingsAction(id, data);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to update quote form settings");
    }
  }
);

export const fetchQuoteFormSettings = createAsyncThunk(
  "quoteFormSettings/getById",
  async (id: string, { rejectWithValue }) => {
    try {
      return await getQuoteFormSettingsByIdAction(id);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch quote form settings");
    }
  }
);

export const fetchQuoteFormSettingsList = createAsyncThunk(
  "quoteFormSettings/get",
  async (query: Record<string, string | number | boolean> = {}, { rejectWithValue }) => {
    try {
      return await getQuoteFormSettingsAction(query);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch quote form settings list");
    }
  }
);

export const fetchQuoteFormSettingsWithFilters = createAsyncThunk(
  "quoteFormSettings/getWithFilters",
  async ({ filters, sort, page, limit }: { filters?: QuoteFormSettingsFilters; sort?: QuoteFormSettingsSort[]; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      return await getQuoteFormSettingsWithFiltersAction(filters, sort, page, limit);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch quote form settings with filters");
    }
  }
);

export const deleteQuoteFormSettings = createAsyncThunk(
  "quoteFormSettings/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteQuoteFormSettingsAction(id);
      return id;
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to delete quote form settings");
    }
  }
);

// Initial State
const initialState: QuoteFormSettingsState = {
  settings: [],
  currentSetting: null,
  loading: false,
  error: null,
  totalCount: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

// Quote Form Settings Slice
const quoteFormSettingsSlice = createSlice({
  name: "quoteFormSettings",
  initialState,
  reducers: {
    clearQuoteFormSettingsError: (state) => {
      state.error = null;
    },
    clearCurrentQuoteFormSettings: (state) => {
      state.currentSetting = null;
    },
    clearQuoteFormSettings: (state) => {
      state.settings = [];
      state.currentSetting = null;
      state.totalCount = 0;
      state.hasNextPage = false;
      state.hasPreviousPage = false;
    }
  },
  extraReducers: (builder) => {
    // Create Quote Form Settings
    builder
      .addCase(createQuoteFormSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuoteFormSettings.fulfilled, (state, action: PayloadAction<QuoteFormSettings>) => {
        state.loading = false;
        state.settings.unshift(action.payload);
        state.totalCount += 1;
        state.error = null;
      })
      .addCase(createQuoteFormSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Quote Form Settings
    builder
      .addCase(updateQuoteFormSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuoteFormSettings.fulfilled, (state, action: PayloadAction<QuoteFormSettings>) => {
        state.loading = false;
        const index = state.settings.findIndex(setting => setting.id === action.payload.id);
        if (index !== -1) {
          state.settings[index] = action.payload;
        }
        if (state.currentSetting?.id === action.payload.id) {
          state.currentSetting = action.payload;
        }
        state.error = null;
      })
      .addCase(updateQuoteFormSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Quote Form Settings by ID
    builder
      .addCase(fetchQuoteFormSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuoteFormSettings.fulfilled, (state, action: PayloadAction<QuoteFormSettings>) => {
        state.loading = false;
        state.currentSetting = action.payload;
        state.error = null;
      })
      .addCase(fetchQuoteFormSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get All Quote Form Settings
    builder
      .addCase(fetchQuoteFormSettingsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuoteFormSettingsList.fulfilled, (state, action: PayloadAction<QuoteFormSettingsResponse>) => {
        state.loading = false;
        state.settings = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
        state.hasNextPage = action.payload.hasNextPage || false;
        state.hasPreviousPage = action.payload.hasPreviousPage || false;
        state.error = null;
      })
      .addCase(fetchQuoteFormSettingsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Quote Form Settings with Filters
    builder
      .addCase(fetchQuoteFormSettingsWithFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuoteFormSettingsWithFilters.fulfilled, (state, action: PayloadAction<QuoteFormSettingsResponse>) => {
        state.loading = false;
        state.settings = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
        state.hasNextPage = action.payload.hasNextPage || false;
        state.hasPreviousPage = action.payload.hasPreviousPage || false;
        state.error = null;
      })
      .addCase(fetchQuoteFormSettingsWithFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete Quote Form Settings
    builder
      .addCase(deleteQuoteFormSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteQuoteFormSettings.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.settings = state.settings.filter(setting => setting.id !== action.payload);
        if (state.currentSetting?.id === action.payload) {
          state.currentSetting = null;
        }
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.error = null;
      })
      .addCase(deleteQuoteFormSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

// Export actions
export const {
  clearQuoteFormSettingsError,
  clearCurrentQuoteFormSettings,
  clearQuoteFormSettings
} = quoteFormSettingsSlice.actions;

// Export reducer
export default quoteFormSettingsSlice.reducer;
